/** Shared, accessible field patterns for the screener filter surface. */

import type { ReactNode } from "react";
import { cn } from "../../lib/utils";
import { Input } from "../ui/input";

export interface FilterOption<T extends string> {
  label: string;
  value: T;
}

/** Toggles one member of a controlled multi-select filter. */
export function toggleFilterValue<T>(values: readonly T[], value: T): T[] {
  return values.includes(value) ? values.filter((item) => item !== value) : [...values, value];
}

function nullableNumber(value: string): number | null {
  if (value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function filterButtonClass(active: boolean) {
  return cn(
    "inline-flex min-h-8 items-center rounded-md border px-2.5 py-1 text-xs font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
    active
      ? "border-accent bg-accent-soft text-accent"
      : "border-border bg-surface-elevated text-muted-foreground hover:border-border-strong hover:text-foreground"
  );
}

/** Groups related filter fields under an accessible legend. */
export function FilterSection({ label, children }: { label: string; children: ReactNode }) {
  return (
    <fieldset className="space-y-1.5">
      <legend className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </legend>
      {children}
    </fieldset>
  );
}

const DATE_PRESETS = [
  { label: "Today", span: 0 },
  { label: "7d", span: 7 },
  { label: "30d", span: 30 },
  { label: "90d", span: 90 },
  { label: "YTD", span: "ytd" }
] as const;

function toIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function presetRange(span: (typeof DATE_PRESETS)[number]["span"]): { from: string; to: string } {
  const today = new Date();
  const to = toIsoDate(today);
  if (span === "ytd") return { from: `${today.getFullYear()}-01-01`, to };
  const from = new Date(today);
  from.setDate(from.getDate() - span);
  return { from: toIsoDate(from), to };
}

/** Renders a controlled date range with common presets. */
export function DateRange({
  label,
  from,
  to,
  onFromChange,
  onToChange
}: {
  label: string;
  from: string | null;
  to: string | null;
  onFromChange: (value: string | null) => void;
  onToChange: (value: string | null) => void;
}) {
  const applyPreset = (span: (typeof DATE_PRESETS)[number]["span"]) => {
    const range = presetRange(span);
    onFromChange(range.from);
    onToChange(range.to);
  };

  return (
    <FilterSection label={label}>
      <div className="flex flex-wrap gap-1">
        {DATE_PRESETS.map((preset) => {
          const range = presetRange(preset.span);
          const active = from === range.from && to === range.to;
          return (
            <button
              key={preset.label}
              type="button"
              aria-label={`${label}: ${preset.label}`}
              aria-pressed={active}
              className={filterButtonClass(active)}
              onClick={() => applyPreset(preset.span)}
            >
              {preset.label}
            </button>
          );
        })}
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        <label className="space-y-1 text-xs text-muted-foreground">
          <span>{label} from</span>
          <Input
            type="date"
            className="h-9 px-2 text-sm"
            value={from ?? ""}
            onChange={(event) => onFromChange(event.target.value || null)}
          />
        </label>
        <label className="space-y-1 text-xs text-muted-foreground">
          <span>{label} to</span>
          <Input
            type="date"
            className="h-9 px-2 text-sm"
            value={to ?? ""}
            onChange={(event) => onToChange(event.target.value || null)}
          />
        </label>
      </div>
    </FilterSection>
  );
}

/** Renders controlled minimum and maximum number fields. */
export function NumberRange({
  label,
  minimum,
  maximum,
  onMinimumChange,
  onMaximumChange
}: {
  label: string;
  minimum: number | null;
  maximum: number | null;
  onMinimumChange: (value: number | null) => void;
  onMaximumChange: (value: number | null) => void;
}) {
  return (
    <FilterSection label={label}>
      <div className="grid grid-cols-2 gap-1.5">
        <label className="space-y-1 text-xs text-muted-foreground">
          <span>Minimum {label.toLowerCase()}</span>
          <Input
            type="number"
            className="h-9 px-2 text-sm"
            value={minimum ?? ""}
            onChange={(event) => onMinimumChange(nullableNumber(event.target.value))}
          />
        </label>
        <label className="space-y-1 text-xs text-muted-foreground">
          <span>Maximum {label.toLowerCase()}</span>
          <Input
            type="number"
            className="h-9 px-2 text-sm"
            value={maximum ?? ""}
            onChange={(event) => onMaximumChange(nullableNumber(event.target.value))}
          />
        </label>
      </div>
    </FilterSection>
  );
}

/** Renders a controlled set of toggle buttons. */
export function OptionButtons<T extends string>({
  options,
  selected,
  ariaLabelPrefix,
  onChange
}: {
  options: readonly FilterOption<T>[];
  selected: readonly T[];
  ariaLabelPrefix?: string;
  onChange: (values: T[]) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1">
      {options.map((option) => {
        const active = selected.includes(option.value);
        return (
          <button
            key={option.value}
            type="button"
            aria-label={ariaLabelPrefix ? `${ariaLabelPrefix}: ${option.label}` : undefined}
            aria-pressed={active}
            className={filterButtonClass(active)}
            onClick={() => onChange(toggleFilterValue(selected, option.value))}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/** Returns the shared visual treatment for a standalone filter toggle. */
export function standaloneFilterButtonClass(active: boolean): string {
  return filterButtonClass(active);
}
